import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import VerifiedSchoolChip from '../../components/UI/VerifiedSchoolChip';
import { universityBadge } from '../Social/universityTheme';
import { HomeTitle } from './HomeSectionHead';
import { Reveal } from '../../components/UI/Reveal';
import { toPlainText } from '../../components/UI/RichText';
import TextRotate from '../../components/UI/TextRotate';
import { PresenceDot } from '../../components/UI/PresenceIndicator';
import HomeV1Hero from './HomeV1Hero';
import HomeHowItWorks from './HomeHowItWorks';
import HomeAcademyCrossSell from './HomeAcademyCrossSell';
import HomeClosingCTA from './HomeClosingCTA';
import HomeFeaturedServices from './HomeFeaturedServices';
import HomeMobile from './HomeMobile';
import SiteSearch from '../../components/Search/SiteSearch';
import Seo from '../../seo/Seo';
import { PAGE_META } from '../../seo/siteMeta';
import { organizationSchema, websiteSchema } from '../../seo/structuredData';
import { formatUSD } from '../../utils/money';

// Ordered so that no two adjacent words sit on the same axis — verified,
// ability, price, dependability, attitude, output, credential, ability again.
// Talented/Skilled and Vetted/Qualified are the two near-pairs, so each is
// parked at the opposite end of the cycle from its twin.
//
// The longest word here decides where the headline breaks; "Qualified" (9)
// is the current worst case and the h1's clamp below is sized around it. A
// longer word means re-checking that clamp — "Professional" (12) used to be in
// this list and was the reason the hero re-flowed as the words cycled.
// "Affordable" was dropped (2026-10-16): selling on price undercuts the brand.
const HERO_ROTATING_WORDS = [
  'Vetted',
  'Talented',
  'Reliable',
  'Driven',
  'Polished',
  'Qualified',
  'Skilled',
];

// ─── Small helpers (ported from Home) ─────────────────────────────────────────
// Darkened from the original brass tones — three of the six only cleared
// ~2-3:1 against the light text/tint pairings these render against (same
// fix already applied to Inbox/DirectMessages.jsx's identical palette).
const AVATAR_COLORS = ['#3B2E16', '#7A5C1F', '#6E5527', '#6B6252', '#5C4726', '#6B5220'];

function formatBudget(job) {
  if (typeof job.budget !== 'number') return null;
  const amount = formatUSD(job.budget);
  if (job.budgetType === 'hourly') return `${amount}/hr`;
  if (job.budgetType === 'negotiable') {
    return typeof job.budgetMax === 'number' && job.budgetMax > job.budget
      ? `${amount} to ${formatUSD(job.budgetMax)}`
      : `${amount}+`;
  }
  return amount;
}

function getAvatarColor(firstName, lastName) {
  const str = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(firstName, lastName) {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Each chip searches Hire Talent, so each must match live services (checked
// 2026-10-03; "Tutoring" and "Coding" used to land on an empty page).
const HERO_CHIPS = ['Logo design', 'Video editing', 'Photography', 'Web development', 'Motion graphics'];

// 12: the cards carry stats now, and the API only computes them for a page that small.
const FREELANCER_RAIL_CAP = 12;

// Phones and tablets get their own page (HomeMobile) instead of the starfield
// hero. Touch plus width, not width alone: a half-width laptop window keeps the
// desktop page (the user's call, 2026-10-19).
const MOBILE_QUERY = '(max-width: 1100px) and (pointer: coarse)';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setMobile(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return mobile;
}


// ─── Page ─────────────────────────────────────────────────────────────────────
export default function HomeV1() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [freelancers, setFreelancers] = useState([]);
  const [featuredList, setFeaturedList] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [freelancersLoading, setFreelancersLoading] = useState(true);
  const [featuredServicesLoading, setFeaturedServicesLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);

  // The spotlight lives in the talent rail (layout A, 2026-10-16): this
  // week's freelancers lead it, then everyone else (deduped).
  const railPeople = [...featuredList, ...freelancers.filter((f) => !featuredList.some((x) => x._id === f._id))];

  const railRef = useRef(null);
  // `off` once the reader takes the wheel (arrow, swipe, wheel, keyboard) —
  // permanent, since fighting a drift you didn't ask for is worse than a
  // static rail. `hover` is the temporary pause. Refs, not state, so neither
  // restarts the rAF loop below.
  const railDriftRef = useRef({ off: false, hover: false });
  const reduceMotion = useReducedMotion();

  // Scroll the talent rail a whole number of cards, so an arrow press always
  // lands on a card edge instead of leaving a sliver of the next one clipped.
  // Card width is read off the DOM rather than hardcoded — it changes with the
  // gap and with any future responsive card size.
  const scrollRail = (dir) => {
    const rail = railRef.current;
    const card = rail?.firstElementChild;
    if (!rail || !card) return;
    railDriftRef.current.off = true; // hand the rail over once they steer it
    const step = card.getBoundingClientRect().width + 16; // gap-4
    const cards = Math.max(1, Math.floor(rail.clientWidth / step));
    rail.scrollBy({ left: dir * cards * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    fetch(`/api/freelancers?limit=${FREELANCER_RAIL_CAP}&page=1&stats=1`)
      .then((r) => r.json())
      .then((d) => {
        const all = d.freelancers || [];
        setFreelancers(
          all.filter((f) => f.profilePhoto && f.profilePhoto.trim() !== '').slice(0, FREELANCER_RAIL_CAP)
        );
      })
      .catch(() => {})
      .finally(() => setFreelancersLoading(false));
  }, []);

  useEffect(() => {
    fetch('/api/freelancers/featured')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFeaturedList(d?.freelancers || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/listings/featured')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFeaturedServices(d?.listings || []))
      .catch(() => {})
      .finally(() => setFeaturedServicesLoading(false));
  }, []);

  // Drift the talent rail once it's actually on screen, ping-ponging at the
  // ends so there's no seam from a jump back to zero. rAF rather than a
  // scrollBy interval: a repeated smooth-scroll reads as a stutter, and the
  // browser cancels an in-flight smooth scroll every time the next one fires.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || reduceMotion || freelancersLoading || railPeople.length < 2) return undefined;

    let visible = false;
    let dir = 1;
    let last = 0;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.4 });
    io.observe(rail);

    let raf = requestAnimationFrame(function tick(now) {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(now - (last || now), 50); // clamp: a backgrounded tab shouldn't lurch
      last = now;
      const { off, hover } = railDriftRef.current;
      if (off || hover || !visible) return;
      const max = rail.scrollWidth - rail.clientWidth;
      if (max <= 1) return;
      let next = rail.scrollLeft + dir * dt * 0.03; // ~30px/s
      if (next >= max) { next = max; dir = -1; }
      else if (next <= 0) { next = 0; dir = 1; }
      rail.scrollLeft = next;
    });

    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [reduceMotion, freelancersLoading, railPeople.length]);

  useEffect(() => {
    fetch('/api/job-postings?limit=6&page=1')
      .then((r) => r.json())
      .then((d) => setJobs(d.postings || []))
      .catch(() => {})
      .finally(() => setJobsLoading(false));
  }, []);

  // ─── Sections ──────────────────────────────────────────────────────────────
  // Order (layout A, 2026-10-16): Where to begin, How it works, Latest jobs,
  // Featured services, Meet the talent, Academy, closing CTA. Dark and light
  // sections alternate, and every content type appears once.
  const pathSection = (
    <>
        {/* ═══ PICK YOUR PATH — three ways in ══════════════════════════════ */}
        <section className="px-6 py-16 sm:px-10 lg:px-16" aria-label="Where to begin">
          <HomeTitle title="Where to begin" desc="Three ways in: hire someone, find work, or learn a skill." />

          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Freelancers */}
            <Reveal className="flex flex-col rounded-[14px] border border-press-border bg-press-card px-8 py-10">
              <p className="mb-4 text-[12px] font-bold text-press-brass-ink">
                For Freelancers
              </p>
              <h2 className="mb-3 text-balance font-press-display text-[1.55rem] font-normal leading-[1.2] tracking-[-0.008em] text-press-ink">
                Turn your skills into income
              </h2>
              <p className="mb-7 text-sm leading-[1.75] text-press-muted">
                Create a listing, set your rate, and start connecting with clients who value your
                university affiliation.
              </p>
              <div className="mt-auto flex flex-col gap-2.5">
                <Link
                  to="/find-work"
                  className="rounded-full bg-press-ink px-5 py-2.5 text-center text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
                >
                  Post a service
                </Link>
                <Link
                  to="/job-postings"
                  className="rounded-full border border-press-border px-5 py-2.5 text-center text-sm font-semibold text-press-ink transition hover:border-press-ink/40"
                >
                  Browse jobs
                </Link>
              </div>
            </Reveal>

            {/* Clients */}
            <Reveal delay={80} className="flex flex-col rounded-[14px] border border-press-border bg-press-card px-8 py-10">
              <p className="mb-4 text-[12px] font-bold text-press-brass-ink">
                For Clients
              </p>
              <h2 className="mb-3 text-balance font-press-display text-[1.55rem] font-normal leading-[1.2] tracking-[-0.008em] text-press-ink">
                Hire verified students and alumni
              </h2>
              <p className="mb-7 text-sm leading-[1.75] text-press-muted">
                Post a job or browse listings to find skilled people ready to take on your project.
              </p>
              <div className="mt-auto flex flex-col gap-2.5">
                <Link
                  to="/post-a-job"
                  className="rounded-full bg-press-ink px-5 py-2.5 text-center text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
                >
                  Post a job
                </Link>
                <Link
                  to="/hire-talent"
                  className="rounded-full border border-press-border px-5 py-2.5 text-center text-sm font-semibold text-press-ink transition hover:border-press-ink/40"
                >
                  Browse services
                </Link>
              </div>
            </Reveal>

            {/* Academy — ink panel */}
            <Reveal delay={160} className="flex flex-col rounded-[14px] bg-press-ink px-8 py-10">
              <p className="mb-4 text-[12px] font-bold text-press-brass-light">
                UF Academy
              </p>
              <h2 className="mb-3 text-balance font-press-display text-[1.55rem] font-normal leading-[1.2] tracking-[-0.008em] text-press-paper">
                Learn from the people who once sat where you are today
              </h2>
              <p className="mb-7 text-sm leading-[1.75] text-press-paper/60">
                Courses, live webinars, tutorials, and podcasts, taught by alumni and working professionals. Every instructor is vetted before they publish.
              </p>
              <Link
                to="/academy"
                className="mt-auto rounded-full bg-press-paper px-5 py-2.5 text-center text-sm font-semibold text-press-ink transition hover:bg-press-paper/90"
              >
                Explore the Academy
              </Link>
            </Reveal>
          </div>
        </section>
    </>
  );

  const featuredSection = (featuredServicesLoading || featuredServices.length > 0) && (
    <HomeFeaturedServices listings={featuredServices} loading={featuredServicesLoading} />
  );

  const jobsSection = (
    <>
        {/* ═══ THE JOB BOARD — moved up front, freelancers shouldn't have to
            scroll past four sections to see real open postings ═══════════ */}
        <section className="px-6 pb-16 sm:px-10 lg:px-16" aria-label="The job board">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0 flex-1">
              <HomeTitle title="Latest jobs" desc="Open postings from clients hiring students and alumni." />
            </div>
            <Link
              to="/job-postings"
              className="whitespace-nowrap text-[13px] font-semibold text-press-brass-ink transition hover:text-press-ink"
            >
              View all →
            </Link>
          </div>

          <div className="mt-8">
            {jobsLoading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-[196px] rounded-[14px] border border-press-border bg-press-soft-tertiary" />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <p className="py-10 text-center text-sm text-press-muted">
                No job postings yet.{' '}
                <Link to="/post-a-job" className="font-semibold text-press-brass-ink">
                  Post the first one
                </Link>
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {jobs.slice(0, 6).map((job) => {
                  const posterName =
                    job.posterName || job.ownerName
                      ? job.posterName || job.ownerName
                      : job.postedBy
                      ? `${job.postedBy?.firstName || ''} ${job.postedBy?.lastName || ''}`.trim()
                      : null;
                  const displayName = posterName || 'Anonymous';
                  const avatarColor = getAvatarColor(
                    job.postedBy?.firstName || displayName.split(' ')[0] || 'A',
                    job.postedBy?.lastName || displayName.split(' ')[1] || ''
                  );
                  const avatarInitials =
                    getInitials(
                      job.postedBy?.firstName || displayName.split(' ')[0] || 'A',
                      job.postedBy?.lastName || displayName.split(' ')[1] || ''
                    ) || displayName.charAt(0).toUpperCase();
                  return (
                    <Link
                      key={job._id}
                      to={`/job-postings/${job._id}`}
                      className="flex flex-col gap-3 rounded-[14px] border border-press-border bg-press-card p-6 transition hover:border-press-ink/40 hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-col gap-2">
                          <span className="self-start rounded-full bg-press-soft px-2.5 py-1 text-[12px] font-semibold text-press-brass-ink">
                            {job.category}
                          </span>
                          <h3 className="font-press-display text-[1.15rem] font-normal leading-[1.25] text-press-ink">
                            {job.title}
                          </h3>
                        </div>
                        {formatBudget(job) && (
                          <span className="flex-none font-press-display text-[1.1rem] text-press-ink">
                            {formatBudget(job)}
                          </span>
                        )}
                      </div>

                      {job.description && (
                        <p className="line-clamp-3 text-[13px] leading-[1.65] text-press-muted">
                          {job.description}
                        </p>
                      )}

                      <div className="mt-auto flex items-center justify-between border-t border-press-border pt-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold"
                            style={{ background: `${avatarColor}22`, color: avatarColor }}
                          >
                            {avatarInitials}
                          </span>
                          <span className="text-[12px] text-press-muted">{displayName}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[12px] text-press-muted">
                          <span>{timeAgo(job.createdAt)}</span>
                          {job.locationType && (
                            <>
                              <span className="h-1 w-1 rounded-full bg-press-border" />
                              <span>{job.locationType}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
    </>
  );

  const talentSection = (
    <>
        {/* ═══ MEET YOUR NEXT HIRE — freelancer rail ═══════════════════════ */}
        {/* A full-width paper-2 band (the 100vmax shadow paints the viewport
            edges without widening the page) so this rail and the white
            spotlight above it read as two sections, not one. */}
        <section
          className="bg-press-paper-2 px-6 py-16 shadow-[0_0_0_100vmax_rgb(var(--press-paper-2))] [clip-path:inset(0_-100vmax)] sm:px-10 lg:px-16"
          aria-label="UniFreelancer Talent"
        >
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0 flex-1">
              <HomeTitle title="Meet the talent" desc="Verified students and alumni available for work." />
            </div>
            <Link
              to="/browse-freelancers"
              className="whitespace-nowrap text-[13px] font-semibold text-press-brass-ink transition hover:text-press-ink"
            >
              View all →
            </Link>
          </div>

          <div className="relative mt-8">
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollRail(-1)}
              className="absolute -left-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-press-border bg-press-card text-press-ink shadow-md transition hover:border-press-ink/40 hover:text-press-brass-ink sm:flex"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollRail(1)}
              className="absolute -right-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-press-border bg-press-card text-press-ink shadow-md transition hover:border-press-ink/40 hover:text-press-brass-ink sm:flex"
            >
              ›
            </button>

            <div
              ref={railRef}
              onMouseEnter={() => { railDriftRef.current.hover = true; }}
              onMouseLeave={() => { railDriftRef.current.hover = false; }}
              onFocusCapture={() => { railDriftRef.current.hover = true; }}
              onBlurCapture={() => { railDriftRef.current.hover = false; }}
              onPointerDown={() => { railDriftRef.current.off = true; }}
              onWheel={() => { railDriftRef.current.off = true; }}
              onKeyDown={() => { railDriftRef.current.off = true; }}
              className="flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {freelancersLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-[460px] w-[264px] flex-none rounded-[22px] bg-press-card" />
                  ))
                : railPeople.map((f, i) => {
                    // Portrait card (Direction A, 2026-10-08): photo first, a
                    // band in the person's school colour, then a three-cell
                    // strip so rating, work done and price compare at a glance.
                    // Stats come from GET /api/freelancers?stats=1.
                    const profile = f.freelancerProfile || {};
                    const school = profile.university || '';
                    const band = school ? universityBadge(school).primary : '#12161C';
                    const headline = toPlainText(profile.headline || '') || toPlainText(profile.bio || '');
                    const { jobsCompleted = 0, rating = null, reviewCount = 0 } = f.stats || {};
                    return (
                      <Link
                        key={`${f._id}-${i}`}
                        to={`/freelancer/${f._id}`}
                        className="group w-[264px] flex-none rounded-[22px] bg-press-ink/[0.03] p-1.5 ring-1 ring-press-ink/[0.06] transition duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-press-ink motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                      >
                        <div className="overflow-hidden rounded-[17px] bg-press-card shadow-[0_18px_40px_-28px_rgba(18,22,28,0.35)]">
                          <div className="relative aspect-[4/5] overflow-hidden" style={{ background: `linear-gradient(155deg, #12161C 10%, ${band})` }}>
                            <span className="absolute inset-0 flex items-center justify-center font-press-display text-[64px] italic text-white">
                              {`${f.firstName?.[0] || ''}${f.lastName?.[0] || ''}`.toUpperCase()}
                            </span>
                            {f.profilePhoto && (
                              <img
                                src={f.profilePhoto}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                              />
                            )}
                            <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[12px] font-semibold text-press-ink">
                              <PresenceDot online={!!f.isOnline} size={8} />
                              {f.isOnline ? 'Online now' : 'View profile'}
                            </span>
                          </div>
                          <div className="h-[3px]" style={{ background: band }} />
                          <div className="p-4">
                            <p className="truncate font-press-display text-[1.25rem] leading-tight text-press-ink">
                              {f.firstName} {f.lastName}
                            </p>
                            <p className="mt-1 line-clamp-2 min-h-[2.6em] text-[13px] leading-snug text-press-muted">{headline}</p>
                            {school && <VerifiedSchoolChip university={school} className="mt-3 max-w-full px-2.5" />}
                            <div className="mt-4 grid grid-cols-3 divide-x divide-press-border rounded-xl bg-press-paper-2 py-2.5 text-center">
                              <div>
                                <p className="text-[14px] font-semibold text-press-ink">
                                  {reviewCount > 0 ? <><span className="text-press-brass" aria-hidden="true">★</span> {rating}</> : 'New'}
                                </p>
                                <p className="text-[12px] text-press-muted">{reviewCount > 0 ? `${reviewCount} review${reviewCount === 1 ? '' : 's'}` : 'no reviews yet'}</p>
                              </div>
                              <div>
                                <p className="text-[14px] font-semibold tabular-nums text-press-ink">{jobsCompleted}</p>
                                <p className="text-[12px] text-press-muted">jobs done</p>
                              </div>
                              <div>
                                <p className="text-[14px] font-semibold tabular-nums text-press-ink">{f.fromPrice ? formatUSD(f.fromPrice) : 'Quote'}</p>
                                <p className="text-[12px] text-press-muted">{f.fromPrice ? 'from' : 'on request'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
              {!freelancersLoading && railPeople.length > 0 && (
                <Link
                  to="/browse-freelancers"
                  className="group w-[264px] flex-none rounded-[22px] bg-press-ink/[0.03] p-1.5 ring-1 ring-press-ink/[0.06] transition duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-press-ink motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  <div className="flex h-full flex-col items-center justify-center gap-4 rounded-[17px] bg-press-ink p-6 text-center text-press-paper shadow-[0_18px_40px_-28px_rgba(18,22,28,0.35)]">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-press-paper/30 text-[22px] transition group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" aria-hidden="true">
                      →
                    </span>
                    <p className="font-press-display text-[1.25rem] leading-tight">View all talent</p>
                    <p className="text-[13px] leading-snug text-press-paper/70">Browse every verified student and alumnus.</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>
    </>
  );


  if (isMobile) {
    return (
      <>
        <Seo {...PAGE_META.home} jsonLd={[organizationSchema(), websiteSchema()]} />
        <HomeMobile
          user={user}
          chips={HERO_CHIPS}
          services={featuredServices}
          servicesLoading={featuredServicesLoading}
          jobs={jobs}
          jobsLoading={jobsLoading}
          people={railPeople}
          peopleLoading={freelancersLoading}
          formatBudget={formatBudget}
          timeAgo={timeAgo}
        />
      </>
    );
  }

  return (
    <div className="theme-press-serif min-h-screen bg-press-paper font-academy">
      <Seo {...PAGE_META.home} jsonLd={[organizationSchema(), websiteSchema()]} />
      {/* ═══ HERO — brass star grains converge into the mark ═══════════════ */}
      <HomeV1Hero>
        <div className="relative z-10 mx-auto w-full max-w-page px-6 sm:px-10 lg:px-16">
          {/* Below 1100px the logo is dropped (see HomeV1Hero), so the
              headline steps up to own the space on its own. */}
          <div className="max-w-[760px]">
            <h1 className="font-press-display text-[clamp(2rem,4.4vw,3.2rem)] max-[1100px]:text-[clamp(2.5rem,6.4vw,4rem)] font-normal leading-[1.12] tracking-[-0.015em] text-press-paper">
              Find your{' '}
              {/* Two atoms, so the headline can only break in places that read
                  well no matter which word is showing:

                  1. the rotating word + "student" — the word was previously
                     glued to "your" instead, which left the break point free
                     to fall between the word and "student". "Professional" is
                     the longest of the five, so it alone pushed "student" onto
                     the next line, and the whole headline re-flowed every
                     2.2s as the words cycled. Bound to "student" instead, the
                     word can never be separated from it.
                  2. "freelancer today." — on a phone "today." was landing on
                     a line of its own. A widow like that is only possible if
                     the two words can be split, so they no longer can.

                  "Find your" is deliberately left loose as the break that
                  stays available on the narrowest screens. Deliberately NOT
                  text-balance: that re-optimises the break on every rotation,
                  which is the jitter these atoms exist to stop. */}
              <span className="whitespace-nowrap">
                <TextRotate
                  texts={HERO_ROTATING_WORDS}
                  mainClassName="overflow-hidden align-baseline text-press-brass-light"
                  splitLevelClassName="overflow-hidden"
                  splitBy="words"
                  rotationInterval={2200}
                  transition={{ type: 'spring', damping: 26, stiffness: 300 }}
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: '-120%', opacity: 0 }}
                />{' '}
              </span>{' '}
              <span className="whitespace-nowrap">freelancer today.</span>
            </h1>

            {/* Search — same matching (pages, categories, Help articles,
                freelancers, services, jobs, courses) as the topbar's search,
                just dressed as the hero's pill instead of the compact bar. */}
            <div className="mt-8 max-w-[560px]">
              <SiteSearch user={user} variant="hero" />
            </div>

            {/* Category chips */}
            <div className="mt-4 flex flex-wrap gap-2">
              {HERO_CHIPS.map((cat) => (
                <Link
                  key={cat}
                  to={`/hire-talent?search=${encodeURIComponent(cat)}`}
                  className="inline-block rounded-full border border-press-paper/20 bg-press-paper/[0.06] px-3.5 py-1.5 text-xs font-medium text-press-paper/75 transition hover:border-press-ink/40 hover:text-press-paper"
                >
                  {cat}
                </Link>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3.5">
              <Link
                to="/hire-talent"
                className="rounded-full bg-press-paper px-6 py-3 text-sm font-semibold text-press-ink transition hover:bg-press-paper/90"
              >
                Hire a freelancer
              </Link>
              <Link
                to="/job-postings"
                className="rounded-full border border-press-paper/30 px-6 py-3 text-sm font-semibold text-press-paper transition hover:bg-press-paper/10"
              >
                Browse jobs
              </Link>
            </div>
          </div>
        </div>
      </HomeV1Hero>

      <div className="mx-auto max-w-page">
        {pathSection}
        <HomeHowItWorks />
        {jobsSection}
        {featuredSection}
        {talentSection}
      </div>

      {/* ═══ ACADEMY — ink cross-sell (full bleed) ═══════════════════════ */}
      <HomeAcademyCrossSell />

      {/* ═══ CLOSING CTA ═════════════════════════════════════════════════ */}
      <HomeClosingCTA />

    </div>
  );
}
